import * as XLSX from "xlsx";

const soNumeros = valor => String(valor === undefined || valor === null ? "" : valor).replace(/\D/g, "");

const cpfValido = cpf => {
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  const dv = (base, pesoInicial) => {
    const soma = base.split("").reduce((acc, n, i) => acc + parseInt(n, 10) * (pesoInicial - i), 0);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };
  return dv(cpf.slice(0, 9), 10) === parseInt(cpf[9], 10) && dv(cpf.slice(0, 10), 11) === parseInt(cpf[10], 10);
};

const cnpjValido = cnpj => {
  if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) return false;
  const dv = base => {
    const pesos = base.length === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const soma = base.split("").reduce((acc, n, i) => acc + parseInt(n, 10) * pesos[i], 0);
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };
  return dv(cnpj.slice(0, 12)) === parseInt(cnpj[12], 10) && dv(cnpj.slice(0, 13)) === parseInt(cnpj[13], 10);
};

export const documentoValido = doc => cpfValido(doc) || cnpjValido(doc);

const lerArquivo = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Não foi possível ler o arquivo."));
    reader.onload = e => resolve(e.target.result);
    reader.readAsArrayBuffer(file);
  });

// Lê uma planilha (XLS, XLSX ou CSV) e devolve os CPFs/CNPJs válidos, sem repetição.
// Usa a coluna "cpf", "cnpj" ou "documento" quando existir; senão varre todas as células.
export const extrairDocumentos = async file => {
  const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
  if (![".xls", ".xlsx", ".csv"].includes(ext)) throw new Error("Selecione um arquivo XLS, XLSX ou CSV");

  let matriz;
  try {
    const buffer = await lerArquivo(file);
    const workbook = ext === ".csv"
      ? XLSX.read(new TextDecoder("utf-8").decode(buffer), { type: "string", raw: true })
      : XLSX.read(buffer, { type: "array", raw: true });
    matriz = [];
    workbook.SheetNames.forEach(nome => {
      matriz = matriz.concat(XLSX.utils.sheet_to_json(workbook.Sheets[nome], { header: 1, raw: true, defval: "", blankrows: false }));
    });
  } catch (e) {
    throw new Error("Não foi possível ler o arquivo. Verifique se ele não está corrompido.");
  }

  // procura uma coluna de documento pelo cabeçalho
  let coluna = -1;
  for (const linha of matriz.slice(0, 15)) {
    const i = linha.findIndex(c => /^(cpf|cnpj|cpf\/cnpj|cnpj\/cpf|documento|doc)$/i.test(String(c).trim()));
    if (i !== -1) { coluna = i; break; }
  }

  const candidatos = [];
  matriz.forEach(linha => {
    const celulas = coluna === -1 ? linha : [linha[coluna]];
    celulas.forEach(c => {
      if (typeof c === "number" && coluna !== -1) {
        // números perdem zeros à esquerda no Excel
        const texto = String(Math.round(c));
        candidatos.push(texto.length <= 11 ? texto.padStart(11, "0") : texto.padStart(14, "0"));
      } else {
        const numeros = soNumeros(c);
        if (numeros.length === 11 || numeros.length === 14) candidatos.push(numeros);
      }
    });
  });

  const vistos = new Set();
  const validos = [];
  let ignorados = 0;
  candidatos.forEach(doc => {
    if (vistos.has(doc)) return;
    vistos.add(doc);
    if (documentoValido(doc)) validos.push(doc);
    else ignorados += 1;
  });

  if (!validos.length) throw new Error("Nenhum CPF ou CNPJ válido foi encontrado na planilha.");
  return { documentos: validos, ignorados };
};
